// react
import { useEffect, useState } from 'react';

// api
import { useLazyGetDataQuery } from '@/api/recalcV2/endpoints/dataApiSlice';

// relative
import { FLOW_DATATYPE, POWER_DATATYPE } from '../utils';

import { DateTime } from 'luxon';

/**
 * Helper to format timestamp for API
 */
function formatTimestampForAPI(timestamp) {
  return encodeURIComponent(DateTime.fromISO(timestamp).toISO());
}

/**
 * Helper to get UnitTypeInputId for a specific dataType from unit's RecordedDataTypes
 */
function getUnitTypeInputIdForDataType(unit, dataType) {
  if (!unit.RecordedDataTypes || !Array.isArray(unit.RecordedDataTypes))
    return null;

  const recordedType = unit.RecordedDataTypes.find(
    (rt) => rt.DataTypes && rt.DataTypes.includes(dataType),
  );

  return recordedType?.UnitTypeInput?.UnitTypeInputId ?? null;
}
/**
 * Aggregate daily data to daily
 */
function aggregateToDaily(dailyData) {
  if (!dailyData || dailyData.length === 0) return [];

  const dailyMap = new Map();
  const todayStart = DateTime.now().startOf('day');

  dailyData.forEach((point) => {
    const dt = DateTime.fromISO(point.timestamp);
    const dayStart = dt.startOf('day');

    // Skip today's data
    if (dayStart >= todayStart) return;

    const dayStartISO = dayStart.toISO();

    if (!dailyMap.has(dayStartISO)) {
      dailyMap.set(dayStartISO, {
        timestamp: dayStartISO,
        volume: 0,
        energy: 0,
        count: 0,
      });
    }

    const day = dailyMap.get(dayStartISO);
    day.volume += point.volume || 0;
    day.energy += point.energy || 0;
    day.count += 1;
  });

  return Array.from(dailyMap.values()).map((day) => ({
    timestamp: day.timestamp,
    volume: day.volume,
    energy: day.energy,
  }));
}

/**
 * Aggregate daily data to weekly
 */
function aggregateToWeekly(dailyData) {
  if (!dailyData || dailyData.length === 0) return [];

  const weeklyMap = new Map();

  dailyData.forEach((point) => {
    const dt = DateTime.fromISO(point.timestamp);
    const weekStart = dt.startOf('week').toISO();

    if (!weeklyMap.has(weekStart)) {
      weeklyMap.set(weekStart, {
        timestamp: weekStart,
        volume: 0,
        energy: 0,
        count: 0,
      });
    }

    const week = weeklyMap.get(weekStart);
    week.volume += point.volume || 0;
    week.energy += point.energy || 0;
    week.count += 1;
  });

  return Array.from(weeklyMap.values()).map((week) => ({
    timestamp: week.timestamp,
    volume: week.volume,
    energy: week.energy,
  }));
}

/**
 * Aggregate daily data to monthly
 */
function aggregateToMonthly(dailyData) {
  if (!dailyData || dailyData.length === 0) return [];

  const monthlyMap = new Map();

  dailyData.forEach((point) => {
    const dt = DateTime.fromISO(point.timestamp);
    const monthStart = dt.startOf('month').toISO();

    if (!monthlyMap.has(monthStart)) {
      monthlyMap.set(monthStart, {
        timestamp: monthStart,
        volume: 0,
        energy: 0,
        count: 0,
      });
    }

    const month = monthlyMap.get(monthStart);
    month.volume += point.volume || 0;
    month.energy += point.energy || 0;
    month.count += 1;
  });

  return Array.from(monthlyMap.values()).map((month) => ({
    timestamp: month.timestamp,
    volume: month.volume,
    energy: month.energy,
  }));
}

/**
 * Custom hook to fetch compressed air data from individual units
 *
 * Fetches data for ALL paired units (for datagrid meter summaries).
 * Aggregates data ONLY for selected units (for charts/KPIs).
 *
 * @param {Object} params
 * @param {Array} params.pairedUnits - All paired units to fetch data for
 * @param {Array} params.selectedUnitIds - Selected unit IDs for aggregation
 * @param {string} params.startTimestamp - ISO timestamp
 * @param {string} params.endTimestamp - ISO timestamp
 * @param {string} params.aggregation - 'day', 'week', or 'month'
 * @returns {Object} { data, isLoading, isError, error }
 */
export default function useCompressedAirData({
  pairedUnits,
  selectedUnitIds,
  startTimestamp,
  endTimestamp,
  aggregation = 'day',
}) {
  const [trigger] = useLazyGetDataQuery();
  const [aggregatedData, setAggregatedData] = useState([]);
  const [hourData, setHourData] = useState([]);
  const [meterSummaries, setMeterSummaries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (
      !pairedUnits ||
      pairedUnits.length === 0 ||
      !startTimestamp ||
      !endTimestamp
    ) {
      setAggregatedData([]);
      setMeterSummaries([]);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setIsError(false);
    setError(null);

    const fetchData = async () => {
      try {
        // Fetch data for ALL paired units (not just selected)
        const promises = pairedUnits.map(async (unit) => {
          const flowInputId = getUnitTypeInputIdForDataType(
            unit,
            FLOW_DATATYPE,
          );
          const powerInputId = getUnitTypeInputIdForDataType(
            unit,
            POWER_DATATYPE,
          );

          const results = {};

          // Fetch flow data if available
          if (flowInputId !== null) {
            const flowResult = await trigger({
              from: formatTimestampForAPI(startTimestamp),
              to: formatTimestampForAPI(endTimestamp),
              unitId: unit.UnitId,
              datatype: FLOW_DATATYPE,
              unitTypeInputId: flowInputId,
              aggregateType: 'Hour', // Always fetch hourly data
            });
            results.flow =
              flowResult?.data?.map((d) => ({
                timestamp: d.timestamp,
                value: (d.value * 60) / 1000, // convert from l/min to m3/h
              })) || [];
          }

          // Fetch power data if available
          if (powerInputId !== null) {
            const powerResult = await trigger({
              from: formatTimestampForAPI(startTimestamp),
              to: formatTimestampForAPI(endTimestamp),
              unitId: unit.UnitId,
              datatype: POWER_DATATYPE,
              unitTypeInputId: powerInputId,
              aggregateType: 'Hour', // Always fetch hourly data
            });
            results.power =
              powerResult?.data?.map((d) => ({
                ...d,
                // API value is W for an hourly bucket; convert to kW so sums are kWh.
                value: (d.value || 0) / 1000,
              })) || [];
          }

          return { unit, results };
        });

        const allResults = await Promise.all(promises);

        if (!isMounted) return;

        // Aggregate data by timestamp
        const timestampMap = new Map();
        const summaries = [];

        allResults.forEach(({ unit, results }) => {
          let totalVolume = 0;
          let totalEnergy = 0;

          // Check if this unit is selected (for aggregation in charts)
          const isSelected =
            selectedUnitIds && selectedUnitIds.includes(unit.UnitId);

          // Process flow data (volume)
          if (results.flow) {
            results.flow.forEach((point) => {
              const ts = point.timestamp || point.Timestamp;
              const value = point.value || point.Value || 0;

              // Always calculate totals for meter summaries
              totalVolume += value;

              // Only aggregate in timestampMap if unit is selected (for charts)
              if (isSelected) {
                if (!timestampMap.has(ts)) {
                  timestampMap.set(ts, {
                    timestamp: ts,
                    volume: 0,
                    energy: 0,
                    hasFlow: false,
                    hasPower: false,
                  });
                }
                const agg = timestampMap.get(ts);
                agg.volume += value;
                agg.hasFlow = true;
              }
            });
          }

          // Process power data (energy/consumption)
          if (results.power) {
            results.power.forEach((point) => {
              const ts = point.timestamp || point.Timestamp;
              const value = point.value || point.Value || 0;

              // Always calculate totals for meter summaries
              totalEnergy += value;

              // Only aggregate in timestampMap if unit is selected (for charts)
              if (isSelected) {
                if (!timestampMap.has(ts)) {
                  timestampMap.set(ts, {
                    timestamp: ts,
                    volume: 0,
                    energy: 0,
                    hasFlow: false,
                    hasPower: false,
                  });
                }
                const agg = timestampMap.get(ts);
                agg.energy += value;
                agg.hasPower = true;
              }
            });
          }

          // Create meter summary for this unit (always, for datagrid)
          summaries.push({
            UnitId: unit.UnitId,
            Name: unit.Name,
            TotalVolume: totalVolume,
            TotalEnergy: totalEnergy,
          });
        });

        // Convert map to array
        let hourData = Array.from(timestampMap.values())
          .map((point) => ({
            timestamp: point.timestamp,
            volume: point.volume,
            energy: point.energy,
            hasFlow: point.hasFlow,
            hasPower: point.hasPower,
          }))
          .sort(
            (a, b) =>
              DateTime.fromISO(a.timestamp).toMillis() -
              DateTime.fromISO(b.timestamp).toMillis(),
          );

        // Apply local aggregation based on selected aggregation level
        let finalAggregated;
        if (aggregation === 'hour') {
          finalAggregated = hourData;
        } else if (aggregation === 'week') {
          finalAggregated = aggregateToWeekly(aggregateToDaily(hourData));
        } else if (aggregation === 'month') {
          finalAggregated = aggregateToMonthly(aggregateToDaily(hourData));
        } else {
          // 'day' (default)
          finalAggregated = aggregateToDaily(hourData);
        }

        setAggregatedData(finalAggregated);
        setHourData(hourData);
        setMeterSummaries(summaries);
      } catch (err) {
        if (isMounted) {
          setIsError(true);
          setError(err);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [
    pairedUnits,
    selectedUnitIds,
    startTimestamp,
    endTimestamp,
    aggregation,
    trigger,
  ]);
  return {
    data: {
      AggregatedData: aggregatedData,
      HourData: hourData,
      MeterSummaries: meterSummaries,
      StackedData: [], // Not used for compressed air
    },
    isLoading: false,
    isFetching: isLoading,
    isError,
    error,
  };
}
